import { useQueryClient } from '@tanstack/vue-query';

/**
 *
 */
export function useReloadOffers() {
    const queryClient = useQueryClient();
    const cartStore = useCartStore();
    const isLoading = ref(false);

    /**
     * Reload offers for the given products
     *
     * This is mainly used for upsells, because upsells don't have a passenger step
     * (where normally the offer is loaded). Therefore the upsell offers are loaded/reloaded
     * in the checkout cart step.
     *
     * Only loads offers if not already loading and if there are products given.
     * Make sure loading state is handled for multiple, concurrent calls.
     *
     * After reloading, clears the cart quote queries to ensure fresh data.
     */
    async function reloadOffers(products: Product[]) {
        if (isLoading.value || products.length === 0) {
            return;
        }

        // Show loading indicator while any of the upsells still loading
        isLoading.value = true;

        const loadingPromises = products.map(product => async () => {
            const passengers = cartStore.passengers.filter(p => product.passengerRefs.includes(p.ref));

            if (product?.config.type === 'P2P') {
                const productP2P = product as ProductP2P;
                const tripUp = productP2P.tripUp;
                const tripDown = productP2P.tripDown;
                const withEPR = productP2P.config.hasEPR ?? false;
                const promotion = productP2P.promotion;
                const listingId = productP2P.config.listingId;

                const { data: offer } = await apiLoadFinalOfferP2P(
                    tripUp,
                    tripDown,
                    withEPR,
                    passengers,
                    promotion,
                    listingId,
                );
                productP2P.offer = offer;
            }
            else if (product?.config.type === 'Activity') {
                const productActivity = product as ProductActivity;

                const activityId = productActivity.activityId ?? '';
                const dateFrom = productActivity.dateFrom;
                const dateUntil = productActivity.dateUntil;
                const promotion = productActivity.promotion;
                const listingId = productActivity.config.listingId;
                const startingPoint = productActivity.startingPoint;
                const option = productActivity.option;

                const { data: offer } = await apiLoadFinalOfferActivity(
                    activityId,
                    dateFrom,
                    dateUntil,
                    passengers,
                    promotion,
                    listingId,
                    startingPoint,
                    option,
                );
                productActivity.offer = offer;
            }
            // Accommodation: do nothing. Why? accommodation offers are already loaded with "combineOffer" earlier in hotel flow
        });

        // Clear quote queries
        queryClient.removeQueries({ queryKey: ['cart-quote'] });
        queryClient.removeQueries({ queryKey: ['cart-quote-with-ancillaries'] });

        // Load all offers and catch errors
        try {
            await Promise.all(loadingPromises.map(fn => fn()));
        }
        catch (error) {
            handleError(error, 'error', 'generic', { products });
        }
        finally {
            isLoading.value = false;
        }
    }

    return {
        isLoading,
        reloadOffers,
    };
}
